import React from 'react';
import { MessageSquare, Calendar, AlertCircle, ArrowUp, Minus, ArrowDown } from 'lucide-react';
import { Task, PRIORITY_CONFIG, TEAM_CONFIG } from '../types';

interface TaskCardProps {
  task: Task;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
  onClick: (task: Task) => void;
  isDragging: boolean;
  compact?: boolean;
}

const PRIORITY_ICONS = {
  urgent: <AlertCircle size={11} className='text-red-600' />,
  high:   <ArrowUp    size={11} className='text-orange-600' />,
  medium: <Minus      size={11} className='text-blue-600' />,
  low:    <ArrowDown  size={11} className='text-gray-400' />,
};

function formatDueDate(dateStr: string): { label: string; overdue: boolean } {
  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    label: due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    overdue: due < today,
  };
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onDragStart,
  onDragEnd,
  onClick,
  isDragging,
  compact = false,
}) => {
  const priorityCfg = PRIORITY_CONFIG[task.priority];
  const teamCfg = TEAM_CONFIG[task.team];
  const due = task.dueDate ? formatDueDate(task.dueDate) : null;
  const isOverdue = due?.overdue === true && task.status !== 'done';

  // ── Compact (backlog) — single horizontal row ──────────────────────────────
  if (compact) {
    return (
      <div
        draggable
        onDragStart={() => onDragStart(task.id)}
        onDragEnd={onDragEnd}
        onClick={() => onClick(task)}
        className={`
          bg-white rounded-lg border px-3 sm:px-4 py-2.5 cursor-pointer
          hover:shadow-sm transition-all duration-150 select-none
          flex items-center gap-2 sm:gap-3
          ${isOverdue ? 'border-red-300 border-l-[3px] border-l-red-500' : 'border-gray-200 hover:border-gray-300'}
          ${isDragging ? 'opacity-40 shadow-lg scale-[1.01]' : 'opacity-100'}
        `}
      >
        {/* Title — fills available space */}
        <p className='text-sm font-medium text-gray-900 truncate flex-1 min-w-0'>
          {task.title}
        </p>

        {/* Divider — desktop only */}
        <div className='hidden sm:block w-px h-4 bg-gray-200 flex-shrink-0' />

        {/* Team badge — desktop only */}
        <span className={`hidden sm:inline text-[10px] font-semibold px-2 py-0.5 rounded flex-shrink-0 ${teamCfg.bg} ${teamCfg.color}`}>
          {task.team}
        </span>

        {/* Labels — desktop only */}
        <div className='hidden sm:flex items-center gap-1 flex-shrink-0'>
          {task.labels.slice(0, 3).map(label => (
            <span key={label} className='text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100'>
              {label}
            </span>
          ))}
          {task.labels.length > 3 && (
            <span className='text-[10px] text-gray-400'>+{task.labels.length - 3}</span>
          )}
        </div>

        {/* Right: priority · key · date · comments · avatar */}
        <div className='flex items-center gap-1.5 sm:gap-3 flex-shrink-0'>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${priorityCfg.bg} ${priorityCfg.color} ${priorityCfg.border}`}>
            {PRIORITY_ICONS[task.priority]}
            <span className='hidden sm:inline'>{priorityCfg.label}</span>
          </span>

          <span className='hidden sm:inline text-[10px] text-gray-400 font-mono'>{task.key}</span>

          {isOverdue && (
            <span className='text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0'>
              Overdue
            </span>
          )}

          {due && (
            <span className={`flex items-center gap-1 text-[10px] font-medium ${isOverdue ? 'text-red-600' : 'text-gray-400'}`}>
              <Calendar size={10} />
              {due.label}
            </span>
          )}

          {task.comments.length > 0 && (
            <span className='hidden sm:flex items-center gap-1 text-[10px] text-gray-400'>
              <MessageSquare size={10} />
              {task.comments.length}
            </span>
          )}

          <div
            className={`w-6 h-6 rounded-full ${task.assignee.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}
            title={task.assignee.name}
          >
            {task.assignee.initials}
          </div>
        </div>
      </div>
    );
  }

  // ── Normal (kanban column) card ────────────────────────────────────────────
  return (
    <div
      draggable
      onDragStart={() => onDragStart(task.id)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(task)}
      className={`
        bg-white rounded-lg border p-3 cursor-pointer
        hover:shadow-md transition-all duration-150 select-none
        ${isOverdue ? 'border-red-300 border-l-[3px] border-l-red-500' : 'border-gray-200 hover:border-gray-300'}
        ${isDragging ? 'opacity-40 shadow-lg rotate-1 scale-105' : 'opacity-100'}
      `}
    >
      {/* Top row: team + labels */}
      <div className='flex items-center gap-1.5 flex-wrap mb-2'>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${teamCfg.bg} ${teamCfg.color}`}>
          {task.team}
        </span>
        {task.labels.map(label => (
          <span key={label} className='text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100'>
            {label}
          </span>
        ))}
      </div>

      {/* Title */}
      <p className='text-sm font-medium text-gray-900 leading-snug mb-3 line-clamp-2'>
        {task.title}
      </p>

      {/* Footer row */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${priorityCfg.bg} ${priorityCfg.color} ${priorityCfg.border}`}>
            {PRIORITY_ICONS[task.priority]}
            {priorityCfg.label}
          </span>
          <span className='text-[10px] text-gray-400 font-mono'>{task.key}</span>
        </div>

        <div className='flex items-center gap-2'>
          {isOverdue && (
            <span className='text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded uppercase tracking-wide'>
              Overdue
            </span>
          )}
          {due && (
            <span className={`flex items-center gap-1 text-[10px] font-medium ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
              <Calendar size={10} />
              {due.label}
            </span>
          )}
          {task.comments.length > 0 && (
            <span className='flex items-center gap-1 text-[10px] text-gray-400'>
              <MessageSquare size={10} />
              {task.comments.length}
            </span>
          )}
          <div
            className={`w-6 h-6 rounded-full ${task.assignee.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}
            title={task.assignee.name}
          >
            {task.assignee.initials}
          </div>
        </div>
      </div>
    </div>
  );
};
